import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TablePagination,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { Edit, Delete, Business, People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useSortTable from '../../../hooks/useSortTable';

const CompanyTable = ({ companies, onEdit, onDelete, page, onPageChange, pagination, canEdit, canDelete }) => {
  const navigate = useNavigate();
  const { sorted, sortBy, sortDir, handleSort } = useSortTable(companies);

  if (!companies || companies.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Business sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <p>No hay empresas registradas</p>
      </Paper>
    );
  }

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1);
  };

  const SortCell = ({ label, field, align, sx: extraSx }) => (
    <TableCell align={align} onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', ...extraSx }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortCell label="Nombre" field="name" />
              <SortCell label="Identificador" field="identifier" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Nombre Corto" field="short" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Email" field="email" sx={{ display: { xs: 'none', md: 'table-cell' } }} />
              <SortCell label="Usuarios" field="_count.users" align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
              <SortCell label="Sitio Web" field="website" sx={{ display: { xs: 'none', lg: 'table-cell' } }} />
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((company) => (
              <TableRow
                key={company.id}
                hover
                onClick={() => navigate(`/companies/${company.id}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {company.imageUrl && (
                      <img
                        src={company.imageUrl}
                        alt={company.name}
                        style={{ width: 32, height: 32, borderRadius: 4 }}
                      />
                    )}
                    <strong>{company.name}</strong>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{company.identifier}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Chip label={company.short} size="small" />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{company.email || '-'}</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Tooltip title="Usuarios activos">
                    <Chip
                      icon={<People />}
                      label={`${company._count?.users || 0}${company.maxUsers ? `/${company.maxUsers}` : ''}`}
                      size="small"
                      color={
                        company.maxUsers && company._count?.users >= company.maxUsers
                          ? 'error'
                          : 'primary'
                      }
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {company.website ? (
                    <a href={company.website} target="_blank" rel="noopener noreferrer">
                      {company.website}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); onEdit(company); }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onDelete(company.id); }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={(page || 1) - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit || 10}
          rowsPerPageOptions={[10]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      )}
    </Paper>
  );
};

export default CompanyTable;
